/**
 * This code is based on the Tiptap `CharacterCount` extension:
 * https://github.com/ueberdosis/tiptap/blob/main/packages/extensions/src/character-count/character-count.ts
 * It has been heavily modified to fit our needs.
 */
import crel from "crelt";
import HTML from "html-parse-stringify";
import type { Node } from "prosemirror-model";
import { type EditorState, Plugin, PluginKey } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { t } from "i18n/i18n";

export interface CharacterCountOptions {
  /**
   * The maximum number of characters that should be allowed. Defaults to `0`.
   * @default null
   * @example 180
   */
  limit: number | null;
  /** Whether to prevent input that exceeds the character limit. Defaults to `false`.
   * @default false
   */
  enforce: boolean;
  /**
   * The mode by which the size is calculated. If set to `textSize`, the textContent of the document is used.
   * If set to `nodeSize`, the nodeSize of the document is used.
   * @default 'textSize'
   * @example 'textSize'
   */
  mode: "textSize" | "nodeSize";
  /**
   * The text counter function to use. Defaults to a simple character count.
   * @default (text) => text.length
   * @example (text) => [...new Intl.Segmenter().segment(text)].length
   */
  textCounter: (text: string) => number;
  /** Determines whether the UI is placed before or after the editor in the DOM.
   * @default "after"
   */
  position: "before" | "after";
  /**
   * The display mode for the character count UI. Can be "NONE", "READ", or "EDIT".
   * - "NONE": Do not display the character count UI.
   * - "READ": Display the character count as read-only text.
   * - "EDIT": Display an input field to set the character limit and a checkbox to enable/disable it.
   * @default "NONE"
   */
  display: "NONE" | "READ" | "EDIT";
}

interface CharacterCountState {
  limit: number | null;
  display: "NONE" | "READ" | "EDIT";
}

const DEFAULT_LIMIT = 600;
const prefix = "ProseMirror-charcount";
export const key = new PluginKey<CharacterCountState>("characterCount");

class CharacterCountView {
  wrapper: HTMLDivElement;

  ui: HTMLDivElement;

  checkbox: HTMLInputElement | null = null;

  input: HTMLInputElement | null = null;

  firstRun: boolean = true;

  constructor(
    readonly editorView: EditorView,
    readonly options: Partial<CharacterCountOptions> = {},
    readonly countCharacters: (node: Node) => number = () => 0,
  ) {
    this.wrapper = crel("div", {
      class: `${prefix}-wrapper relative grow group flex flex-col`,
    }) as HTMLDivElement;
    this.ui = this.wrapper.appendChild(
      crel("div", {
        class: `${prefix} absolute right-3 bottom-1 z-[1]`,
      }),
    ) as HTMLDivElement;

    if (editorView.dom.parentNode) {
      editorView.dom.parentNode.replaceChild(this.wrapper, editorView.dom);
    }

    const position = options.position || "after";
    if (position === "after") {
      this.wrapper.insertBefore(editorView.dom, this.wrapper.firstChild);
    } else {
      this.wrapper.appendChild(editorView.dom);
    }

    this.update(editorView, editorView.state);
  }

  update(editorView: EditorView, prevState: EditorState) {
    const defaults = {
      limit: this.options?.limit || null,
      display: this.options?.display || "NONE",
    };
    const { limit, display } = key.getState(editorView.state) || defaults;
    const count = this.countCharacters(editorView.state.doc);

    const { limit: prevLimit, display: prevDisplay } = key.getState(prevState) || defaults;
    const prevCount = this.countCharacters(prevState.doc);

    // Only update the UI if the count, limit, or display mode has changed
    if (!this.firstRun && count === prevCount && limit === prevLimit && display === prevDisplay) {
      return;
    }
    this.firstRun = false;

    const dom = editorView.dom;
    const limitReached = limit !== null && limit > 0 && count >= limit;
    const limitExceeded = limit !== null && limit > 0 && count > limit;

    // If we are *at* the limit, set a data attribute on the wrapper
    if (limitReached) {
      this.wrapper.dataset.charLimitReached = "true";
    } else if (this.wrapper.dataset.charLimitReached) {
      delete this.wrapper.dataset.charLimitReached;
    }
    // If we have *exceeded* the limit, also set aria-invalid to true on the editor itself
    if (limitExceeded) {
      dom.setAttribute("aria-invalid", "true");
    } else if (dom.getAttribute("aria-invalid") === "true") {
      dom.removeAttribute("aria-invalid");
    }

    const checkboxHasFocus = this.checkbox === document.activeElement;
    const inputHasFocus = this.input === document.activeElement;

    // Clear the UI and re-render it
    this.ui.innerHTML = "";

    if (display === "READ" && limit) {
      this.ui.appendChild(
        crel(
          "div",
          {
            class:
              "rounded border border-transparent bg-blue-tint-95 p-1 text-xs " +
              "group-data-[char-limit-reached=true]:border-red-tint-20 group-data-[char-limit-reached=true]:bg-red-tint-95 group-data-[char-limit-reached=true]:text-red-shade-20",
          },
          [
            limitReached ? `${t("character_counter.reached_limit")} ` : undefined,
            t("character_counter.characters_used_count", {
              textLength: count,
              maxLength: limit,
            }),
          ],
        ),
      );
    } else if (display === "EDIT") {
      this.checkbox = crel("input", {
        type: "checkbox",
        class: "size-4",
        checked: Boolean(limit),
        "aria-label": t("character_counter.toggle_limit"),
      }) as HTMLInputElement;
      this.checkbox.addEventListener("change", (event) => {
        const checked = (event.currentTarget as HTMLInputElement).checked;
        const newLimit = checked ? DEFAULT_LIMIT : null;
        editorView.dispatch(editorView.state.tr.setMeta(key, { limit: newLimit }));
      });
      this.checkbox.addEventListener("mousedown", (e) => {
        e.preventDefault();
        // Clicking on the checkbox normally causes it to focus. Because the checkbox is focused,
        // this triggers the `:focus-within` CSS selector on the wrapper, which
        // causes the toolbar to appear. This in turn causes the character count UI to
        // jump upwards, which is a jarring experience.
        //
        // Instead, we will use the `mousedown` event to trigger a `change` event on the checkbox,
        // allowing the `mousedown` event to toggle the checkbox state. This prevents the character
        // count UI from jumping around due to the toolbar appearing/disappearing.
        const target = e.currentTarget as HTMLInputElement;
        target.checked = !target.checked;
        (e.currentTarget as HTMLInputElement).dispatchEvent(new Event("change", { bubbles: true }));
      });

      this.input = crel("input", {
        type: "number",
        name: "character-limit",
        placeholder: DEFAULT_LIMIT.toString(),
        class:
          "w-[60px] rounded border border-black-tint-90 bg-white p-1 text-black-tint-20 disabled:border-black-tint-90 disabled:bg-blue-tint-95 disabled:text-black-tint-55",
        value: limit ?? "",
        disabled: !limit,
      }) as HTMLInputElement;
      this.input.addEventListener("change", (event) => {
        const value = (event.currentTarget as HTMLInputElement).value;
        const newLimit = value === "" ? null : parseInt(value, 10);
        editorView.dispatch(editorView.state.tr.setMeta(key, { limit: newLimit }));
      });

      // This is what React-i18next uses for interpolating React components,
      // so we need to parse the translated string and insert the checkbox and input in the correct places.
      const parsedText = HTML.parse(t("character_counter.character_limit", { count: limit ?? 0 }));

      this.ui.appendChild(
        crel(
          "div",
          {
            class:
              "inline-flex items-center gap-2 text-sm text-black-tint-40 p-1 rounded bg-blue-tint-95 border border-blue-tint-70",
          },
          [
            this.checkbox,
            parsedText[0].children[0].content,
            this.input,
            parsedText[2].children[0].content,
          ],
        ),
      );

      if (checkboxHasFocus) {
        this.checkbox.focus();
      } else if (inputHasFocus) {
        this.input.focus();
      }
    }
  }

  destroy() {
    if (this.wrapper.parentNode)
      this.wrapper.parentNode.replaceChild(this.editorView.dom, this.wrapper);
  }
}

export function characterCountPlugin(options: Partial<CharacterCountOptions> = {}) {
  const { limit: initialLimit = null, display: initialDisplay = "NONE", enforce = false } = options;
  return new Plugin<CharacterCountState>({
    key,
    state: {
      init() {
        return { limit: initialLimit, display: initialDisplay };
      },
      apply(tr, value) {
        const meta = tr.getMeta(key) as Partial<CharacterCountState> | undefined;
        if (meta) {
          if (typeof meta.limit !== "undefined") {
            // eslint-disable-next-line no-param-reassign
            value = { ...value, limit: meta.limit };
          }
          if (typeof meta.display === "string") {
            // eslint-disable-next-line no-param-reassign
            value = { ...value, display: meta.display };
          }
        }
        return value;
      },
    },
    props: {
      attributes: {
        class: "grow",
      },
    },
    countCharacters(node: Node): number {
      if (options?.mode === "nodeSize") {
        return node.nodeSize;
      }

      const text = node.textBetween(0, node.content.size, "⏎", " ");
      const textCounter = options?.textCounter;
      return textCounter ? textCounter(text) : text.length;
    },
    view(editorView) {
      return new CharacterCountView(editorView, options, this.countCharacters);
    },
    filterTransaction(transaction, state) {
      if (!enforce) return true;

      const { limit } = key.getState(state) || { limit: initialLimit, display: initialDisplay };
      // Nothing has changed or no limit is defined. Ignore it.
      if (!transaction.docChanged || limit === 0 || limit === null || limit === undefined) {
        return true;
      }

      const oldSize = this.spec.countCharacters(state.doc);
      const newSize = this.spec.countCharacters(transaction.doc);

      // Everything is in the limit. Good.
      if (newSize <= limit) {
        return true;
      }

      // The limit has already been exceeded but will be reduced.
      if (oldSize > limit && newSize > limit && newSize <= oldSize) {
        return true;
      }

      // The limit has already been exceeded and will be increased further.
      if (oldSize > limit && newSize > limit && newSize > oldSize) {
        return false;
      }

      const isPaste = transaction.getMeta("paste");

      // Block all exceeding transactions that were not pasted.
      if (!isPaste) {
        return false;
      }

      // For pasted content, we try to remove the exceeding content.
      const pos = transaction.selection.$head.pos;
      const over = newSize - limit;
      const from = pos - over;
      const to = pos;

      // It’s probably a bad idea to mutate transactions within `filterTransaction`
      // but for now this is working fine.
      transaction.deleteRange(from, to);

      // In some situations, the limit will continue to be exceeded after trimming.
      // This happens e.g. when truncating within a complex node (e.g. table)
      // and ProseMirror has to close this node again.
      // If this is the case, we prevent the transaction completely.
      const updatedSize = this.spec.countCharacters(transaction.doc);

      if (updatedSize > limit) {
        return false;
      }

      return true;
    },
  });
}
